import { useEffect, useState, useMemo } from "react";
import { Hash, Settings2, ExternalLink, Link2Off, Search, Pencil, Check } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { PillTabBar } from "@/components/PillTabs";
import { useSubmenuOrder } from "@/lib/useSubmenuOrder";
import { toast } from "sonner";

// Curated universal (Google Material Symbols) icons for order forms.
const ICON_LIBRARY = [
  "phone", "dialpad", "call", "phone_in_talk", "phone_forwarded", "ring_volume", "add_call",
  "phone_enabled", "wifi_calling", "contact_phone", "voicemail", "sms", "chat", "forum",
  "fax", "print", "scanner", "mail", "outgoing_mail", "cloud", "cloud_sync", "dns", "router",
  "lan", "hub", "headset_mic", "support_agent", "business", "apartment", "hotel", "local_hospital",
  "store", "star", "bolt", "language", "public", "swap_horiz", "import_export", "sync_alt",
  "qr_code_2", "numbers", "tag", "format_list_numbered", "dialer_sip", "perm_phone_msg", "tty",
];

const MatSym = ({ name, className = "" }) => <span className={`material-symbols-rounded ${className}`}>{name}</span>;

// The same component powers both the Number Orders page (numbers + toll-free
// + vanity + ports) and the Fax page (fax orders + ports). We filter by
// `category` from the seeded `order_forms` collection so a single admin UI
// keeps managing both.
export function OrderFormsPage({
  category = "numbers",
  title = "Number Orders",
  subtitle = "Submit new and port orders for numbers, toll-free, and vanity.",
  testId = "number-orders-page",
}) {
  const { has, user } = useAuth();
  const canManage = has("apps.edit") || has("order_forms.edit");
  const isSuper = user?.role === "super_admin";
  const [forms, setForms] = useState([]);
  const [activeKey, setActiveKey] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editUrl, setEditUrl] = useState("");
  const [editIcon, setEditIcon] = useState("");
  const [editIconName, setEditIconName] = useState("");
  // Iter 115j — embed source can be a URL or a raw provider snippet, and each
  // form can be hidden from non-managers without losing its configuration.
  const [editCode, setEditCode] = useState("");
  const [editMode, setEditMode] = useState("url");
  const [editHidden, setEditHidden] = useState(false);
  const [iconSearch, setIconSearch] = useState("");
  const [editLayout, setEditLayout] = useState(false);

  // Persisted submenu order — one bucket per category so Number Orders
  // and Fax reorder independently. Reuses the same `settings.submenu_orders`
  // container used by Inventory Hub.
  const menuKey = category === "fax" ? "fax" : "number_orders";
  const { orderedKeys, save: saveOrder } = useSubmenuOrder(menuKey, forms.map((f) => f.form_key));

  const load = () => api.get("/order-forms").then((r) => {
    // Backward-compatible: rows seeded before we added `category` default
    // to the `numbers` bucket (fax rows are always tagged explicitly).
    const filtered = (r.data || []).filter((f) => (f.category || "numbers") === category);
    setForms(filtered);
    setActiveKey((k) => k || (filtered[0] && filtered[0].form_key) || "");
  }).catch(() => {});
  useEffect(() => { load(); }, [category]);

  const active = forms.find((f) => f.form_key === activeKey);

  // Apply Super-Admin-configured order. Any form not present in the saved
  // order stays at the tail in load order so new forms don't disappear.
  const sortedForms = useMemo(() => {
    if (!orderedKeys.length) return forms;
    const rank = new Map(orderedKeys.map((k, i) => [k, i]));
    return [...forms].sort((a, b) => {
      const ra = rank.has(a.form_key) ? rank.get(a.form_key) : Number.MAX_SAFE_INTEGER;
      const rb = rank.has(b.form_key) ? rank.get(b.form_key) : Number.MAX_SAFE_INTEGER;
      return ra - rb;
    });
  }, [forms, orderedKeys]);

  const openEdit = () => {
    setEditUrl(active?.embed_url || "");
    setEditIcon(active?.icon_url || "");
    setEditIconName(active?.icon_name || "");
    setEditCode(active?.embed_code || "");
    setEditMode(active?.embed_mode === "code" ? "code" : "url");
    setEditHidden(!!active?.hidden);
    setIconSearch("");
    setEditOpen(true);
  };
  const onIconFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 512 * 1024) { toast.error("Icon must be under 512 KB"); return; }
    const reader = new FileReader();
    reader.onload = () => { setEditIcon(reader.result); setEditIconName(""); };  // uploaded image wins
    reader.readAsDataURL(file);
  };
  const pickIcon = (name) => { setEditIconName(name); setEditIcon(""); };  // chosen icon replaces image
  const saveUrl = async () => {
    try {
      const { data } = await api.put(`/order-forms/${activeKey}`, {
        embed_url: editUrl, icon_url: editIcon, icon_name: editIconName,
        embed_code: editCode, embed_mode: editMode, hidden: editHidden,
      });
      setForms((arr) => arr.map((f) => (f.form_key === activeKey ? data : f)));
      setEditOpen(false);
      toast.success("Order form updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update"); }
  };

  const renderFormIcon = (f, cls) =>
    f.icon_url ? <img src={f.icon_url} alt={f.label} className="h-full w-full object-cover" />
      : f.icon_name ? <MatSym name={f.icon_name} className={cls} />
        : <Hash className="h-9 w-9" />;

  const filteredIcons = ICON_LIBRARY.filter((n) => n.includes(iconSearch.toLowerCase().replace(/\s+/g, "_")));

  return (
    <div className="space-y-5" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">{title}</h1>
          <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          {canManage && active && (
            <Button variant="outline" size="sm" data-testid="edit-embed-button" onClick={openEdit}>
              <Settings2 className="h-4 w-4" /> Edit form
            </Button>
          )}
          {isSuper && sortedForms.length > 1 && (
            <Button variant="outline" size="sm" data-testid={`order-tabs-edit-layout-${menuKey}`}
              onClick={() => setEditLayout((v) => !v)}>
              {editLayout ? <><Check className="h-4 w-4" /> Done</> : <><Pencil className="h-4 w-4" /> Edit layout</>}
            </Button>
          )}
        </div>
      </div>

      {editLayout && (
        <p className="text-[11px] text-slate-500" data-testid={`order-tabs-edit-hint-${menuKey}`}>
          Drag pills to reorder. Order saves automatically and applies for everyone.
        </p>
      )}

      {/* Iter 90l — pill-tab bar (matches Settings). Was a big icon-tile
          grid; users asked for consistent menu styling across all apps.
          Iter 91d — Super Admin can drag-reorder in edit mode. */}
      <PillTabBar
        testidPrefix="order-tab"
        tabs={sortedForms.map((f) => ({
          key: f.form_key,
          label: f.hidden ? `${f.label} (hidden)` : f.label,
          icon: (() => {
            // Wrap the icon renderer as a React component so PillTabBar can
            // call it uniformly. Material-symbols glyph is rendered inline.
            return () => renderFormIcon(f, "text-[16px] leading-none");
          })(),
        }))}
        activeKey={activeKey}
        onChange={setActiveKey}
        editable={editLayout}
        onReorder={(nextTabs) => saveOrder(nextTabs.map((t) => t.key))}
        className="mb-3"
      />

      {active && (active.embed_mode === "code" && active.embed_code ? (
        <div className="space-y-2">
          {active.hidden && canManage && (
            <p className="text-[11px] font-medium text-amber-600" data-testid="order-hidden-notice">
              This form is hidden — only users who can manage forms see it.
            </p>
          )}
          {/* Iter 115j — provider snippets run inside a sandboxed iframe so the
              embedded markup can never touch the Conduit session. */}
          <div className="relative w-full overflow-hidden rounded-lg border border-slate-200 bg-white" style={{ height: "calc(100vh - 320px)", minHeight: 460 }} data-testid="order-embed-code-container">
            <iframe key={`${active.form_key}-code`} title={active.label} className="h-full w-full" frameBorder="0"
              sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
              srcDoc={`<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>html,body{margin:0;padding:0;font-family:system-ui,sans-serif}iframe{width:100%;min-height:100%;border:0}</style></head><body>${active.embed_code}</body></html>`} />
          </div>
        </div>
      ) : active.embed_url ? (
        <div className="space-y-2">
          {active.hidden && canManage && (
            <p className="text-[11px] font-medium text-amber-600" data-testid="order-hidden-notice">
              This form is hidden — only users who can manage forms see it.
            </p>
          )}
          <div className="relative w-full overflow-hidden rounded-lg border border-slate-200 bg-white" style={{ height: "calc(100vh - 320px)", minHeight: 460 }} data-testid="order-iframe-container">
            <iframe key={active.form_key} src={active.embed_url} title={active.label} className="h-full w-full" frameBorder="0" />
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 py-20 text-center" data-testid="order-not-configured">
          <Link2Off className="h-8 w-8 text-slate-300" />
          <p className="mt-3 font-medium text-slate-600">{active.label} isn&apos;t configured yet</p>
          <p className="text-sm text-slate-400">{canManage ? "Add the embed URL or paste the form's embed code to display it here." : "Please check back later — this form hasn\u2019t been set up yet."}</p>
          {canManage && <Button className="mt-4" size="sm" data-testid="configure-embed-button" onClick={openEdit}><Settings2 className="h-4 w-4" /> Set up form</Button>}
        </div>
      ))}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{active?.label}</DialogTitle>
            <DialogDescription>Set the embed link or code, the button icon, and whether this form is visible.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Button icon</Label>
              <div className="flex items-center gap-3">
                <span className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-[#eef2fb] text-primary">
                  {editIcon ? <img src={editIcon} alt="icon" className="h-full w-full object-cover" />
                    : editIconName ? <MatSym name={editIconName} className="text-[40px] leading-none" />
                      : <Hash className="h-8 w-8" />}
                </span>
                <div className="flex-1 space-y-1.5">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                    <Input value={iconSearch} onChange={(e) => setIconSearch(e.target.value)} placeholder="Search universal icons (phone, fax, sms…)" className="pl-8 text-xs" data-testid="order-icon-search" />
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <label className="cursor-pointer text-primary hover:underline" data-testid="order-icon-upload-label">
                      Upload image
                      <input type="file" accept="image/*" className="hidden" data-testid="order-icon-upload" onChange={onIconFile} />
                    </label>
                    {(editIcon || editIconName) && <button type="button" data-testid="order-icon-remove" onClick={() => { setEditIcon(""); setEditIconName(""); }} className="text-red-500 hover:underline">Clear</button>}
                  </div>
                </div>
              </div>
              <div className="grid max-h-44 grid-cols-8 gap-1.5 overflow-auto rounded-lg border border-slate-200 p-2" data-testid="order-icon-grid">
                {filteredIcons.map((name) => (
                  <button key={name} type="button" title={name} data-testid={`order-icon-${name}`} onClick={() => pickIcon(name)}
                    className={`flex aspect-square items-center justify-center rounded-lg border text-slate-600 transition-colors hover:bg-primary/5 ${editIconName === name ? "border-primary bg-primary/10 text-primary ring-1 ring-primary/30" : "border-slate-100"}`}>
                    <MatSym name={name} className="text-[22px] leading-none" />
                  </button>
                ))}
                {filteredIcons.length === 0 && <p className="col-span-8 py-3 text-center text-xs text-slate-400">No icons match.</p>}
              </div>
              <p className="text-xs text-slate-400">Pick a universal Google icon, or upload a square image (PNG/SVG, &lt; 512 KB). Image overrides the chosen icon.</p>
            </div>
            <div className="space-y-2">
              <Label>Embed source</Label>
              <div className="flex gap-1.5">
                <Button type="button" size="sm" variant={editMode === "url" ? "default" : "outline"}
                  data-testid="order-embed-mode-url" onClick={() => setEditMode("url")}
                  className={editMode === "url" ? "bg-primary text-primary-foreground hover:bg-primary/90" : ""}>Link (URL)</Button>
                <Button type="button" size="sm" variant={editMode === "code" ? "default" : "outline"}
                  data-testid="order-embed-mode-code" onClick={() => setEditMode("code")}
                  className={editMode === "code" ? "bg-primary text-primary-foreground hover:bg-primary/90" : ""}>Embed code</Button>
              </div>
              {editMode === "url" ? (
                <div className="space-y-1.5">
                  <Input data-testid="embed-url-input" value={editUrl} onChange={(e) => setEditUrl(e.target.value)} placeholder="https://forms.example.com/new-number" />
                  {editUrl && <a href={editUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline"><ExternalLink className="h-3 w-3" /> Preview in new tab</a>}
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Textarea data-testid="embed-code-input" rows={7} value={editCode} onChange={(e) => setEditCode(e.target.value)}
                    placeholder={'<iframe src="https://app.pipefy.com/public/form/XXXX" width="100%" height="800"></iframe>'}
                    className="font-mono text-[11px]" />
                  <p className="text-xs text-slate-400">Paste the snippet from your form provider. It runs in a sandboxed frame, isolated from Conduit.</p>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
              <div>
                <div className="text-sm font-medium text-slate-700">Show this form</div>
                <div className="text-xs text-slate-400">Turn off to hide the tab from everyone except users who can manage forms.</div>
              </div>
              <Switch data-testid="order-form-visible-switch" checked={!editHidden} onCheckedChange={(v) => setEditHidden(!v)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button data-testid="save-embed-button" onClick={saveUrl} className="bg-primary hover:bg-primary/90 text-primary-foreground">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Default export = the Number Orders variant (existing /number-orders route).
export default function NumberOrders() {
  return (
    <OrderFormsPage
      category="numbers"
      title="Number Orders"
      subtitle="Submit new and port orders for numbers, toll-free, and vanity."
      testId="number-orders-page"
    />
  );
}
